import qs from "qs";
// eslint-disable-next-line import/no-extraneous-dependencies
import "@babel/polyfill";
import * as Sentry from "@sentry/react";
import camelCase from "./camelCase";
import countArrayValues from "./countArrayValues";
import omitEmpty from "./omitEmpty";

/* =========================================================
 * 現在の企業ポータルコンテキストから企業の name（Rails 側 name; underscore 形式）を返す。
 * 企業ポータル外（通常の activo.jp 等）では null を返す。
 *
 * 判定は以下の優先順で行う:
 *   1. metaタグ（`meta[name="enterprise-portal-name"]`。ポータルlayoutが出力。値は
 *      VolunteerInfoBuyerCompany#name）。カスタムホスト（独自ドメイン）では
 *      ホスト名から企業を推測できないため、これを最優先で読む
 *   2. サブドメイン形式（prod: `{subdomain}.volunteer-portal.jp` / staging: `{subdomain}.volunteer-portal-staging.com` /
 *      dev: `{subdomain}.lvh.me`）の subdomain 部のハイフンをアンダースコアに戻した文字列
 *      （例: `sanyo-trading` → `sanyo_trading`）。NOTE: JS からは Rails の
 *      Settings.enterprise_portal.domain を参照できないため、env 別ドメインをここで列挙する
========================================================= */
const currentCompanyName = () => {
  const metaContent = document.querySelector(
    'meta[name="enterprise-portal-name"]'
  )?.content;
  if (metaContent) return metaContent;

  const host = location.hostname;
  const portalSuffix = [
    ".volunteer-portal.jp",
    ".volunteer-portal-staging.com",
    ".lvh.me",
  ].find((s) => host.endsWith(s));
  if (!portalSuffix) return null;
  const subdomain = host.slice(0, host.length - portalSuffix.length);
  if (!subdomain || subdomain === "www") return null;
  return subdomain.replace(/-/g, "_");
};

/* =========================================================
 * 現在のページが企業ポータルのコンテキストかを返す。
 * 企業ポータル配下では activo top-level 用エンドポイント（例: `/sub_search`）への
 * 非同期 refetch を抑止する判定として使う。
========================================================= */
export const isCompanyPortalContext = () => {
  // === LVJ 旧URL 暫定対応 開始 ===
  if (isLvjLegacyContext()) return true;
  // === LVJ 旧URL 暫定対応 終了 ===

  return currentCompanyName() !== null;
};

/* =========================================================
 * article検索トップページのpathを返す
========================================================= */
export const topPath = (volOrJob) => {
  // === LVJ 旧URL 暫定対応 開始 ===
  const lvjTopPath = lvjLegacyTopPath();
  if (lvjTopPath) return lvjTopPath;
  // === LVJ 旧URL 暫定対応 終了 ===

  if (volOrJob == "job") {
    return Routes.job_searchresult_path();
  }
  return Routes.volunteer_searchresult_path();
};

/* =========================================================
 * article検索APIのpathを返す
========================================================= */
export const basePath = (volOrJob) => {
  // === LVJ 旧URL 暫定対応 開始 ===
  const lvjBasePath = lvjLegacyBasePath();
  if (lvjBasePath) return lvjBasePath;
  // === LVJ 旧URL 暫定対応 終了 ===

  const company_name = currentCompanyName();
  if (company_name) {
    return Routes.api_v1_company_articles_path({ company_name });
  }
  if (volOrJob == "job") {
    return Routes.api_v1_job_articles_path();
  }
  return Routes.api_v1_articles_path();
};

/* =========================================================
 * === LVJ（louisvuitton_japan）旧URL 暫定対応 開始 ===
 *
 * louisvuitton_japan のみ subdomain 移行に未同意のため、`activo.jp/companies/louisvuitton_japan/...`
 * の旧URLで運用継続している（middleware で内部的に subdomain 配下へ rewrite される）。
 * その間、ブラウザURL bar には旧URLが残るため、React 側の URL 生成も旧URL prefix を付与する必要がある。
 *
 * 削除手順（LVJ も subdomain 移行した時）:
 *   1. 上記 topPath / basePath / isCompanyPortalContext 内の LVJ 用呼び出し（コメントマーカー込み）を削除
 *   2. 本セクション（isLvjLegacyContext / lvjLegacyTopPath / lvjLegacyBasePath）を削除
 *   3. 関連backend（app/middleware/louisvuitton_japan_legacy_url_rewriter.rb 他）も併せて削除
========================================================= */
const isLvjLegacyContext = () => location.pathname.startsWith("/companies/");

const lvjLegacyTopPath = () => {
  if (!isLvjLegacyContext()) return null;
  const company_name = location.pathname.split("/")[2];
  return `/companies/${company_name}${Routes.company_searchresult_path()}`;
};

const lvjLegacyBasePath = () => {
  if (!isLvjLegacyContext()) return null;
  const company_name = location.pathname.split("/")[2];
  return Routes.api_v1_company_articles_path({ company_name });
};
// === LVJ 旧URL 暫定対応 終了 ===

/* =========================================================
 * article検索パラメータの短縮名
========================================================= */
export const paramsPairs = {
  kind: "kind",
  genre: "genre",
  characteristic: "chara",
  atmosphere: "atm",
  university: "uni",
  area: "area",
  position: "posi",
  skill: "skill",
  job_type: "jt",
  job_characteristic: "j_chara",
  price_category: "price",
  working_form: "wf",
  tag: "tag",
  service_type: "st",
  charitable_gift: "cg",
  status: "status",
};

/* =========================================================
 * activity_term, activity_frequentの検索クエリ対応表
========================================================= */
export const rangeParamsPairs = {
  activity_term: "term_days",
  activity_frequent: "frequent_per_month",
};

/* =========================================================
 * articleSearchContainerのrequestStateの初期値
========================================================= */
export const initialRequestState = {
  word: "",
  page: 1,
  startDate: null,
  endDate: null,
  areaIds: [],
  genreIds: [],
  tagIds: [],
  characteristicIds: [],
  atmosphereIds: [],
  universityIds: [],
  positionIds: [],
  skillIds: [],
  jobTypeIds: [],
  jobCharacteristicIds: [],
  workingFormIds: [],
  serviceTypeIds: [],
  charitableGiftIds: [],
  kindIds: [],
  priceCategoryIds: [],
  geoPoints: [],
  areaKeywords: [],
};

/* =========================================================
 * 検索パラメータの生成
========================================================= */
export const createRequestParam = (requestState) => {
  const requestParam = {
    word: requestState.word == "" ? null : requestState.word,
    page: requestState.page,
    start_date: requestState.startDate,
    end_date: requestState.endDate,
    sort: requestState.sort === "" ? null : requestState.sort,
    geo_points: requestState.geoPoints,
    area_keywords:
      requestState.areaKeywords.length < 50 ? requestState.areaKeywords : "", // 「海外」選択時のブラウザのurl長さ制限対策;（参考: 全都道府県名 ~ 280文字，全国名 ~ 1800文字）
  };
  Object.keys(paramsPairs).forEach((key) => {
    requestParam[paramsPairs[key]] = requestState[`${camelCase(key)}Ids`];
  });
  Object.values(rangeParamsPairs).forEach((value) => {
    requestParam[`${value}_gte`] = requestState[`${camelCase(value)}Gte`];
    requestParam[`${value}_lte`] = requestState[`${camelCase(value)}Lte`];
  });
  return requestParam;
};

/* =========================================================
 * Seo用ページのinitialState.requestから状態を復元
========================================================= */
export const restoreSeoState = (initialState) => {
  const restoredSeoState = {};
  Object.keys(initialState).forEach((key) => {
    restoredSeoState[camelCase(key)] = initialState[key];
  });
  return restoredSeoState;
};

/* =========================================================
 * クエリストリングのkeyを状態名に書き換えた上でオブジェクトにパース
========================================================= */
export const parseQueryStr = (queryStr, initialState, paramsPairsArg) => {
  const restoredState = { ...initialState };
  const parsedObj = qs.parse(queryStr);
  Object.keys(parsedObj).forEach((key) => {
    const ShortenSearchIdsKey = Object.values(paramsPairsArg).find(
      (val) => val == key
    );
    if (!ShortenSearchIdsKey) {
      restoredState[`${camelCase(key)}`] = parsedObj[key];
    } else {
      const searchIdsKey = Object.keys(paramsPairsArg).find(
        (k) => paramsPairsArg[k] === ShortenSearchIdsKey
      );
      restoredState[`${camelCase(searchIdsKey)}Ids`] = parsedObj[key];
    }
  });
  return restoredState;
};

/* =========================================================
 * 検索URLの生成
========================================================= */
export const stringifyUrl = (urlBasePath, query) => {
  const copyQuery = { ...query };
  if (!query.area_keywords) {
    copyQuery.area_keywords = null;
  }
  const queryUrl = qs.stringify(copyQuery, {
    skipNulls: true,
    arrayFormat: "brackets",
    encode: false,
    sort: (a, b) => a.localeCompare(b),
  });
  // NOTE: queryUrlが空の場合（パラメータがない場合）は?を付けない
  const requestUrl = queryUrl ? `${urlBasePath}?${queryUrl}` : urlBasePath;
  return requestUrl;
};

/* =========================================================
 * 指定されている検索条件を文字列に変換
========================================================= */
export const buildCheckedNames = (searchItems) => {
  let commitment = "";
  for (let i = 0; i < searchItems.length; i += 1) {
    if (!searchItems[i].items) {
    } else if (searchItems[i].title == "予算") {
      if (searchItems[i].priceId) {
        commitment += searchItems[i].items
          .filter((item) => searchItems[i].priceId === item.value)
          .map((item) => item.label);
        commitment += " ";
      }
    } else if (searchItems[i].title == "活動日程") {
      const { startDate } = searchItems[i];
      if (startDate != null) {
        commitment += startDate;
      }
    } else if (
      searchItems[i].title == "期間" ||
      searchItems[i].title == "頻度"
    ) {
      if (searchItems[i].gte || searchItems[i].lte) {
        commitment += searchItems[i].items
          .filter((item) => searchItems[i].gte === item.value)
          .map((item) => item.label);
        commitment += "〜";
        commitment += searchItems[i].items
          .filter((item) => searchItems[i].lte === item.value)
          .map((item) => item.label);
      }
      commitment += " ";
    } else {
      commitment += searchItems[i].items
        .filter((item) => searchItems[i].ids.includes(item.value))
        .map((item) => item.label)
        .join(",");
      if (searchItems[i].ids.length != 0) {
        commitment += " ";
      }
    }
  }
  return commitment;
};

/* =========================================================
 * 検索のエンドポイントを呼び出すヘルバーメソッド
========================================================= */
export const fetchSearch = async (stringUrl) => {
  const response = await fetch(stringUrl, {
    method: "GET",
    headers: {
      Accept: "application/json",
      "content-type": "application/json",
    },
  });
  return response.json();
};

/* =========================================================
 * 検索ヒット数から全ページ数を計算する関数
 * @params total 検索ヒット数
========================================================= */
export const RESULTS_PER_PAGE = 30;

export const buildTotalPages = (total) => {
  if (total === 0) return 1;
  return Math.ceil(total / RESULTS_PER_PAGE);
};

/* =========================================================
 * gaのトラッキング、titleタグの変更など、細かいページ更新をまとめたもの
========================================================= */
export const refreshPage = (newUrl, title, scrollY = 0) => {
  // gaTrackPV(newUrl, title);
  // NOTE: 企業ポータルではサーバが出力したポータル名義のtitleを維持する（activo名義で上書きしない）
  if (!isCompanyPortalContext()) {
    document.title = `${title} | activo（アクティボ）`;
  }
  window.history.pushState(null, null, newUrl);
  window.scrollTo(0, scrollY);
};

/* =========================================================
 * GTM の「リンクのみクリック + タグの配信を待つ(Wait for Tags)」トリガー対策（GTMの動きと競合する不具合対策）
 *
 * （仕組み詳細）
 * GTM はページャーリンクのクリックで遷移を肩代わりするが、
 * その遷移時にリンクの href を読み直す。
 * 一方 react-paginate は「次へ/前へ」クリックで内部 selected を進め、
 * href を「現在ページ+1」へ書き換えるため、GTM が書き換え後(=2つ先)へフル遷移し「1クリックで2ページ進む」不具合になる。
 * GTM は react より先(capture/早い bubble)にクリックを処理し、
 * かつ遷移を遅延実行するため、react 側(bubble)からの伝播停止や自前遷移では競り負ける。
 *
 * （対策）
 * GTM 読み込み時のみ、capture フェーズで早期に介入し、
 * href が書き換わる前(=正しいページ)へ自前フル遷移しつつ、伝播を止めて、
 * react-paginate の href 書き換えとGTM の遷移肩代わりの両方を封じる。
 * GTM非配下(広告ブロック等)では従来どおり SPA に委ねる。
 *
 * NOTE: 真の SPA(リロード無し)を GTM 配下でも維持するには、GTM 側でページャーリンクを
 *       リンククリックトリガーの例外に追加する必要がある。
 *       (=現状も GTM 配下では結局フルリロードしているため、本対応で行き先が正しくなるだけで体感は変わらない)
 * WARNING: 本ガードは GTM の読み込み有無のみで判定する。上記 GTM 側除外を行って真の SPA を
 *          実現する際は、本ガードがフルリロードを強制し SPA を阻害するため、必ず併せて撤去すること。
 *
 * @return リスナー解除関数(useEffect の cleanup 用)
========================================================= */
export const installGtmPagerHijackGuard = () => {
  if (typeof document === "undefined") return () => {};
  const handler = (e) => {
    if (!window.google_tag_manager) return; // GTM 非配下は SPA に委ねる
    const link = e.target?.closest?.(".mainPagenavi a[href]");
    const href = link?.getAttribute("href");
    if (!href) return;
    e.preventDefault();
    e.stopImmediatePropagation(); // GTM・react-paginate 双方のハンドラを止める
    window.location.assign(href); // href 書き換え前の正しいページへ
  };
  document.addEventListener("click", handler, true); // capture: GTM より先に処理
  return () => document.removeEventListener("click", handler, true);
};

/* =========================================================
 * アナリティクスのPVトラッキング
 * gtm側で実行に変更
========================================================= */
// export const gaTrackPV = (page, title) => {
//   ga('set', 'title', title);
//   ga('set', 'page', `${page}?spa=1`);
//   ga('send', 'pageview');
// }

/* =========================================================
 * セレクトされたpriceのid(Str)とパラメータ用のpriceのids(Array)の対応表
========================================================= */
export const priceCategoryIdsSanitizer = (priceId) => {
  const priceIdInt = parseInt(priceId, 10);
  // NOTE: 「選択してください」(空文字)に戻した時はNaNになるため、素通りして「その他」扱いにならないよう解除する
  if (Number.isNaN(priceIdInt)) {
    return [];
  }
  if (priceIdInt < 9) {
    return Array.from(Array(priceIdInt).keys()).map((i) => `${i + 1}`);
  }
  if (priceIdInt == 9 || priceIdInt == 11) {
    return [`${priceIdInt}`];
  }
  return ["10", "11"];
};

/* ============================================================
 * area_id -> geoPoint, areaKeywordへの変換
=============================================================== */
export const areaOrStr = " OR ";
export const areaTextEndKeyword = ";";
export const areaParamsTranslate = (areaIds, opts) => {
  let geoPoints = [];
  let areaKeywords = [];
  opts.forEach((opt) => {
    if (areaIds.includes(opt.value)) {
      if (opt.geo_point) geoPoints = [...geoPoints, opt.geo_point]; // 'online'はgeo_pointがないので対策
      areaKeywords = [...areaKeywords, opt.label];
    } else if (opt.opts) {
      const areaParams = areaParamsTranslate(areaIds, opt.opts);
      geoPoints = [...geoPoints, ...areaParams.geoPoints];
      areaKeywords = [...areaKeywords, ...areaParams.areaKeywords];
    }
  });
  return { geoPoints, areaKeywords };
};

/* =========================================================
 * seo用ページかどうかの判定
========================================================= */
export const isPageForSeo = (currentPath) =>
  !currentPath.includes("searchresult");

/* =========================================================
 * 検索条件保存用に検索条件をJSON化
========================================================= */
export const paramsToJson = (requestParam) => {
  let newParams = JSON.parse(JSON.stringify(requestParam));

  delete newParams.page;
  newParams = omitEmpty(newParams);

  const newParamsJson = JSON.stringify(newParams);
  return newParamsJson;
};

/* =========================================================
 * タイトルの更新
========================================================= */
export const replaceH1text = (requestFromSp, total, title) => {
  if (requestFromSp) {
    $("#jsHeading").html(`<span>${total}</span>件の${title}`);
  } else {
    $("#jsHeading").text(title);
    $("#jsSeoText").hide();
    $("#jsSearchSuggest").hide();
  }
};

/* =========================================================
 * 検索履歴の保存処理(cookieに)
========================================================= */
export const setCookieSearchHistories = (requestParam) => {
  try {
    window.onpopstate = () => {}; // ブラウザバック時は記録しない
    // cookieに保存する文字列を長くしたくないため値がないものと不要なkeyは削除
    const HistoryParam = { ...requestParam };
    const notSaveKeys = ["page", "start_date", "end_date"];
    Object.entries(HistoryParam).forEach(([key, value]) => {
      if (
        !(value && !Number.isFinite(value) && value.length) ||
        notSaveKeys.includes(key)
      ) {
        delete HistoryParam[key];
      }
    });
    if (HistoryParam.vol_or_job == "volunteer") HistoryParam.vol_or_job = "vol"; // 少しでも短く
    // 本当はparam全てをencodeしたいが、文字列が長くなり過ぎないよう日本語の混じる可能性があるwordだけencode
    if (HistoryParam.word) {
      HistoryParam.word = encodeURIComponent(HistoryParam.word);
    }

    if (HistoryParam.area_keywords) {
      HistoryParam.area_keywords = encodeURIComponent(
        HistoryParam.area_keywords
      );
    }

    const cookieName = "search_histories";
    const cookieValue = readCookie(cookieName);
    const searchHistories = cookieValue ? JSON.parse(cookieValue) : [];

    let newsearchHistories = searchHistories.filter(
      (history) => !isEqualObject(history, HistoryParam)
    );
    newsearchHistories = [HistoryParam, ...newsearchHistories]; // 検索履歴を追加する
    if (newsearchHistories.length > 10) newsearchHistories.pop(); // 検索履歴は10件まで

    const expire = new Date();
    expire.setTime(expire.getTime() + 1000 * 3600 * 24 * 30); // 期日を30日間に
    writeCookie(
      cookieName,
      JSON.stringify(newsearchHistories),
      expire.toUTCString()
    );
  } catch (error) {
    Sentry.init({
      // 本番環境のみエラー通知
      dsn:
        process.env.NODE_ENV === "production"
          ? "https://8f781d4beb004ab999d7fd14d84fe1b9@o4504274116018176.ingest.sentry.io/4504274162417664"
          : false,

      integrations: [new BrowserTracing()],
      environment: process.env.NODE_ENV,

      // Set tracesSampleRate to 1.0 to capture 100%
      // of transactions for performance monitoring.
      // We recommend adjusting this value in production
      tracesSampleRate: 0.2,
      // NOTE: Sentry の beforeSend は event を直接変更する想定の API のため param-reassign を許容
      // eslint-disable-next-line no-param-reassign
      beforeSend(event) {
        // Modify the event here
        if (event.user) {
          // Don't send user's email address
          /* eslint-disable no-param-reassign */
          delete event.user.email;
          delete event.user.ip_address;
          delete event.user.username;
          /* eslint-enable no-param-reassign */
        }
        return event;
      },
    });
    Sentry.captureException(error);
    document.cookie = "search_histories=";
  }
};

// =========================================================
// 以下、汎用メソッド
const isEqualObject = (objectA, objectB) =>
  // 検索履歴の中に今回と同様の検索があった場合、除外する(sortして文字列にすれば比較できる)
  JSON.stringify(Object.entries(objectA).sort()) ==
  JSON.stringify(Object.entries(objectB).sort());
const writeCookie = (cookieName, cookieValue, expires = null, path = "/") => {
  let cookie = `${cookieName}=${cookieValue};`;
  if (expires) cookie = `${cookie} expires=${expires};`;
  document.cookie = `${cookie} path=${path};`;
};

const readCookie = (cookieName) => {
  const cookiesArray = document.cookie.split(";");
  const matched = cookiesArray
    .map((c) => c.trim())
    .find((c) => c.match(`^${cookieName}=`));
  if (matched) {
    const cookieValue = matched.replace(`${cookieName}=`, "");
    if (cookieValue) return cookieValue;
  }
  return null;
};
// =========================================================
export const changeHeaderImgAndHref = (volOrJob) => {
  const headerHref = volOrJob == "job" ? Routes.job_path() : Routes.root_path();
  // NOTE: Propshaft 移行に伴い、digest 付き URL を layout の <meta> タグから取得する
  // 注入元: app/views/application/_asset_meta_tags.html.erb
  const metaName =
    volOrJob == "job" ? "asset-logo-work" : "asset-logo-volunteer";
  const headerImg = document.querySelector(`meta[name="${metaName}"]`)?.content;
  $("#jsActivoLogo").attr("href", headerHref);
  if (headerImg) {
    $("#jsActivoLogo").children("img").attr("src", headerImg);
  }
};

export const appendNoindex = () => {
  let metaRobots = document.querySelector('meta[name="robots"]');
  if (!metaRobots) {
    metaRobots = document.createElement("meta");
    metaRobots.setAttribute("name", "robots");
    document.head.appendChild(metaRobots);
    metaRobots.setAttribute("content", "noindex");
  }
};

export const removeNoindex = () => {
  const metaRobots = document.querySelector('meta[name="robots"]');
  if (metaRobots) {
    metaRobots.remove();
  }
};

export const appendCanonicalTag = (href) => {
  let canonical = document.querySelector('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement("link");
    canonical.setAttribute("href", href);
    document.head.appendChild(canonical);
    canonical.setAttribute("rel", "canonical");
  } else {
    canonical.setAttribute("href", href);
  }
};

export const removeCanonicalTag = () => {
  const canonical = document.querySelector('link[rel="canonical"]');
  if (canonical) {
    canonical.remove();
  }
};

export const sortResults = (results) => {
  let newResults = [...results];
  if (
    newResults.length == 0 ||
    resultsByIndividualUserLessThan(6, newResults)
  ) {
    return newResults;
  }
  // 18件目まで、6件毎に、団体の重複があれば、重複しないように並び替える
  for (
    let num = 1, end = 6;
    conditionForSorting(num, end, newResults);
    num += 1, end += 6
  ) {
    const start = end - 6;
    const publishResults = newResults.filter((r) => r.status === 1);
    const closingResults = newResults.filter((r) => r.status === 3);
    const publishNewResults = sortResultsNotOverlappingUsers(
      start,
      end,
      publishResults
    );
    const closingNewResults = sortResultsNotOverlappingUsers(
      start,
      end,
      closingResults
    );
    newResults = publishNewResults.concat(closingNewResults);
  }
  return newResults;
};

// private

// 団体の異なる記事が特定件数未満か
// NOTE: 元コードは return 文がなく常に undefined を返していたが、関数名と用途から本来は比較結果を返すのが意図
const resultsByIndividualUserLessThan = (num, results) =>
  Array.from(new Set(results.map((r) => r.user_id))).length < num;

const conditionForSorting = (num, end, results) =>
  num < 4 &&
  resultsByOverlappingUserMoreThan(num, 0, end, results) &&
  end <= results.length;

// 団体が重複している記事が特定件数以上か
const resultsByOverlappingUserMoreThan = (num, start, end, results) => {
  // 文字列しか重複判定できないので、一旦下記で。広報力UPオプション利用中はソートしないようにtitleを混ぜる
  const targetIds = results
    .map((r) => r.user_id + (r.is_promotion ? r.title : ""))
    .slice(start, end);
  const counts = countArrayValues(targetIds);
  return counts[0].count > num;
};
// 団体が重複しないように記事を並び替え
const sortResultsNotOverlappingUsers = (start, end, results) => {
  const newResults = [...results];
  const targetResults = newResults.slice(start, results.length);
  const individualUserIds = Array.from(
    new Set(targetResults.map((r) => r.user_id))
  );
  const targetUserIds = individualUserIds.slice(1, end - start);
  const targetResultsVal = [...targetResults];
  targetUserIds.forEach((userId, index) =>
    moveAt(
      targetResultsVal,
      indexOfFirstResultsBy(userId, targetResults),
      index + 1
    )
  );
  const sortedResults = newResults.slice(0, start).concat(targetResultsVal);
  return sortedResults;
};

const moveAt = (results, index, at) => {
  if (index === at || index > results.length - 1 || at > results.length - 1) {
    return results;
  }
  const value = results[index];
  const tail = results.slice(index + 1);
  results.splice(index);
  Array.prototype.push.apply(results, tail);
  results.splice(at, 0, value);
  return results;
};

// resultsInRangeのうち、特定団体の最初の記事のindex
const indexOfFirstResultsBy = (userId, resultsInRange) => {
  const targetResult = resultsInRange
    .filter((r) => r.user_id === userId)
    .shift();
  const targetIndex = resultsInRange.map((r) => r.id).indexOf(targetResult.id);
  return targetIndex;
};
